import { Checkbox, CheckboxGroup } from '@eigtech/ui/components/checkbox'
import { FC, ReactNode } from 'react'
import { Control, FieldValues, useController } from 'react-hook-form'
import type { RequireAtLeastOne } from 'type-fest'
import { WithFieldNameAndDefaultValueProps } from '../types/FieldProps'
import { BaseProps, FieldControl } from './FieldControl'

type CheckboxProps = React.ComponentProps<typeof Checkbox>

export type CheckboxGroupFieldProps<TFieldValues extends FieldValues = FieldValues> = Omit<
  WithFieldNameAndDefaultValueProps<CheckboxGroupContainerProps, TFieldValues>,
  'children'
> &
  RequireAtLeastOne<
    {
      children?: ReactNode
      options?: { value: string; label?: ReactNode; checkboxProps?: CheckboxProps }[]
      checkboxProps?: CheckboxProps
    },
    'children' | 'options'
  >

export function CheckboxGroupField<TFieldValues extends FieldValues = FieldValues>({
  children,
  options,
  checkboxProps: baseCheckboxProps,
  ...props
}: CheckboxGroupFieldProps<TFieldValues>) {
  return (
    <CheckboxGroupContainer {...props}>
      {children ??
        (options ?? []).map(({ value, label, checkboxProps }) => (
          <CheckboxGroupCheckbox
            key={value}
            disabled={props.disabled}
            {...baseCheckboxProps}
            {...checkboxProps}
            name={props.name}
            value={value}
          >
            {label ?? value}
          </CheckboxGroupCheckbox>
        ))}
    </CheckboxGroupContainer>
  )
}

export type CheckboxGroupFieldComponent<TFieldValues extends FieldValues = FieldValues> =
  typeof CheckboxGroupField<TFieldValues>

//

export interface CheckboxGroupContainerProps extends BaseProps {
  defaultValue?: unknown[]
  stackProps?: React.ComponentProps<'div'>
  children: React.ReactNode
  allValues?: string[]
}

export const CheckboxGroupContainer: FC<CheckboxGroupContainerProps> = ({
  name,
  label,
  control,
  children,
  stackProps,
  defaultValue,
  allValues,
  ...props
}: CheckboxGroupContainerProps) => {
  const {
    field: { ref, name: __name, onChange, ...field },
  } = useController({
    control,
    name,
    defaultValue: defaultValue || [],
  })

  return (
    <FieldControl control={control} label={label} name={name} {...props}>
      <CheckboxGroup {...field} allValues={allValues} onValueChange={onChange}>
        <div ref={ref} className="mt-1 flex flex-col gap-1 pl-4" {...stackProps}>
          {children}
        </div>
      </CheckboxGroup>
    </FieldControl>
  )
}

export interface CheckboxGroupCheckboxProps extends CheckboxProps {
  name: string
  label?: ReactNode
  children?: ReactNode
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control?: Control<any, any>
}

export const CheckboxGroupCheckbox: FC<CheckboxGroupCheckboxProps> = (
  props: CheckboxGroupCheckboxProps
) => {
  const { name, control, label, children, value, ...rest } = props
  const {
    formState: {
      // errors,
      isSubmitting,
    },
  } = useController({ name, control })

  // const error = get(errors, name, '')

  if (label || children) {
    return (
      <label className="flex items-center gap-2">
        <Checkbox disabled={isSubmitting} value={value} {...rest} />

        {label}
        {children}
      </label>
    )
  }

  return <Checkbox disabled={isSubmitting} value={value} {...rest} />
}
